/**
 * AppShell.tsx — Main layout with horizontal MashfaTopNav.
 * No sidebar. Page content fills everything below the nav.
 */

import { lazy, Suspense, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resolveRouteTitleKey } from './Breadcrumb'
import MashfaTopNav from './MashfaTopNav'
import MashfaSidebar from './MashfaSidebar'
import { UpdateBanner } from '../UpdateBanner'
import { LicenseExpiryBanner } from '../LicenseExpiryBanner'
import { usePrefetchOnLogin } from '../../hooks/usePrefetchOnLogin'

const CommandPalette = lazy(() => import('./CommandPalette'))
const FeedbackFAB = lazy(() => import('../feedback/FeedbackFAB'))

export default function AppShell({ children }: { children?: React.ReactNode }) {
  const [cmdOpen, setCmdOpen] = useState(false)
  const { prefetch } = usePrefetchOnLogin()
  const location = useLocation()
  const { t, i18n } = useTranslation()

  // Prefetch master data once on mount
  useEffect(() => {
    prefetch()
  }, [prefetch])

  // Per-route browser-tab title so each open page is identifiable in the tab
  // strip (every tab used to read the same "HeilPort v…" string). Reuses the
  // Breadcrumb route→title map; detail routes fall back to their section name.
  useEffect(() => {
    const pageTitle = t(resolveRouteTitleKey(location.pathname))
    document.title = pageTitle ? `${pageTitle} · HEILPORT v${__APP_VERSION__}` : `HEILPORT v${__APP_VERSION__}`
  }, [location.pathname, i18n.language, t])

  // Ctrl+K shortcut for Command Palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen(v => !v)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Open the command palette from the topnav quick-search field (custom event)
  useEffect(() => {
    const open = () => setCmdOpen(true)
    window.addEventListener('open-command-palette', open)
    return () => window.removeEventListener('open-command-palette', open)
  }, [])

  // Close command palette on route change
  useEffect(() => { setCmdOpen(false) }, [location.pathname])

  return (
    <>
      <UpdateBanner />
      <LicenseExpiryBanner />

      {/* minHeight subtracts the phase-0 demo banner height when present */}
      <div style={{ minHeight: 'calc(100vh - var(--mashfa-banner-h, 0px))', display: 'flex', flexDirection: 'column' }}>
        <MashfaTopNav />

        {/* Sidebar (right in RTL via dir='rtl' on html) + main content */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <MashfaSidebar />
          {/* Page content — pages control their own padding / fullbleed */}
          <main className="app-main" style={{ flex: 1, minWidth: 0 }}>
            {children}
          </main>
        </div>
      </div>

      {/* Command Palette (Ctrl+K) */}
      {cmdOpen && (
        <Suspense fallback={null}>
          <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
        </Suspense>
      )}

      {/* In-app feedback FAB — gates itself on feature flag + permission */}
      <Suspense fallback={null}>
        <FeedbackFAB />
      </Suspense>
    </>
  )
}
